import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useWallet } from '../contexts/ServiceContext';
import ContentFlagging from './ContentFlagging';
import DAOVoting from './DAOVoting';
import AdminPanel from './AdminPanel';
import { ReputationService } from '../services/ReputationService';
import DAOGovernanceService from '../services/DAOGovernanceService';
import AdminPanelService from '../services/AdminPanelService';
import ProofOfHumanityService from '../services/ProofOfHumanityService';
import { sanitizeErrorMessage } from '../utils/security';
import './DAOIntegration.css';

/**
 * DAO Integration Component
 * Main interface for all DAO-related features including flagging, voting, and admin functions
 */
const DAOIntegration = () => {
  const { address: walletAddress, isConnected } = useWallet();
  const [userReputation, setUserReputation] = useState(null);
  const [userTokenBalance, setUserTokenBalance] = useState(0);
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [pendingProposals, setPendingProposals] = useState([]);
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Services
  const reputationService = new ReputationService();
  const daoService = new DAOGovernanceService();
  const adminService = new AdminPanelService();
  const humanityService = new ProofOfHumanityService();

  useEffect(() => {
    if (isConnected && walletAddress) {
      loadUserDAOData();
    } else {
      resetUserData();
    }
  }, [isConnected, walletAddress]);

  const loadUserDAOData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load user reputation and token balance
      const [reputation, tokenBalance] = await Promise.all([
        reputationService.getUserReputation(walletAddress),
        daoService.getTokenBalance(walletAddress)
      ]);

      setUserReputation(reputation);
      setUserTokenBalance(tokenBalance);

      // Check admin permissions
      const permissions = await adminService.checkAdminPermissions(walletAddress);
      setAdminPermissions(permissions);

      // Load DAO data
      const [proposals, queue] = await Promise.all([
        daoService.getActiveProposals(),
        permissions.hasPermission ? adminService.getModerationQueue() : Promise.resolve({ queue: [] })
      ]);

      setPendingProposals(proposals);
      setModerationQueue(permissions.hasPermission ? queue.queue : []);

    } catch (error) {
      console.error('Error loading DAO data:', sanitizeErrorMessage(error));
      setError('Failed to load DAO data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetUserData = () => {
    setUserReputation(null);
    setUserTokenBalance(0);
    setAdminPermissions(null);
    setPendingProposals([]);
    setFlaggedContent([]);
    setModerationQueue([]);
  };

  const handleFlagSubmitted = async (result) => {
    // Refresh data after successful flag submission
    await loadUserDAOData();
    setActiveModal(null);
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const getReputationTierColor = (tier) => {
    const colors = {
      'BRONZE': '#cd7f32',
      'SILVER': '#c0c0c0',
      'GOLD': '#ffd700',
      'PLATINUM': '#e5e4e2',
      'DIAMOND': '#b9f2ff'
    };
    return colors[tier] || '#6c757d';
  };

  const formatTokenBalance = (balance) => {
    if (balance >= 1000000) {
      return `${(balance / 1000000).toFixed(1)}M`;
    } else if (balance >= 1000) {
      return `${(balance / 1000).toFixed(1)}K`;
    }
    return balance.toLocaleString();
  };

  if (!isConnected || !walletAddress) {
    return (
      <div className="dao-integration">
        <div className="dao-connect-prompt">
          <div className="connect-icon">🏛️</div>
          <h3>DAO Governance</h3>
          <p>Connect your wallet to participate in community governance, content moderation, and reputation building.</p>
          <div className="dao-features">
            <div className="feature-item">
              <span className="feature-icon">🚩</span>
              <span>Flag inappropriate content</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🗳️</span>
              <span>Vote on community proposals</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⭐</span>
              <span>Build reputation and earn rewards</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🛡️</span>
              <span>Moderate content (with sufficient tokens)</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dao-integration">
      {/* User DAO Status */}
      <div className="dao-user-status">
        <div className="status-header">
          <h3>🏛️ DAO Governance Status</h3>
          {loading && <div className="loading-indicator">Loading...</div>}
        </div>

        <div className="status-cards">
          {/* Reputation Card */}
          <div className="status-card reputation-card">
            <div className="card-header">
              <span className="card-icon">⭐</span>
              <span className="card-title">Reputation</span>
            </div>
            <div className="card-content">
              <div className="reputation-score">{userReputation?.score || 0}</div>
              <div 
                className="reputation-tier"
                style={{ color: getReputationTierColor(userReputation?.tier) }}
              >
                {userReputation?.tier || 'BRONZE'}
              </div>
              <div className="reputation-progress">
                <div 
                  className="progress-bar"
                  style={{ 
                    width: `${userReputation?.progressToNext || 0}%`,
                    backgroundColor: getReputationTierColor(userReputation?.tier)
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Token Balance Card */}
          <div className="status-card token-card">
            <div className="card-header">
              <span className="card-icon">🪙</span>
              <span className="card-title">ZNOOZINU Tokens</span>
            </div>
            <div className="card-content">
              <div className="token-balance">{formatTokenBalance(userTokenBalance)}</div>
              <div className="token-label">Available for staking</div>
            </div>
          </div>

          {/* Voting Power Card */}
          <div className="status-card voting-card">
            <div className="card-header">
              <span className="card-icon">🗳️</span>
              <span className="card-title">Voting Power</span>
            </div>
            <div className="card-content">
              <div className="voting-power">
                {userReputation ? Math.floor(userTokenBalance * (1 + userReputation.score / 1000)) : userTokenBalance}
              </div>
              <div className="voting-label">
                {userReputation ? `${userTokenBalance} tokens + ${userReputation.score} reputation` : 'Base tokens only'}
              </div>
            </div>
          </div>

          {/* Admin Status Card */}
          {adminPermissions?.hasPermission && (
            <div className="status-card admin-card">
              <div className="card-header">
                <span className="card-icon">🛡️</span>
                <span className="card-title">Admin Role</span>
              </div>
              <div className="card-content">
                <div className="admin-role">{adminPermissions.role}</div>
                <div className="admin-queue">{moderationQueue.length} items in queue</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DAO Actions */}
      <div className="dao-actions">
        <div className="actions-header">
          <h4>DAO Actions</h4>
        </div>

        <div className="action-buttons">
          <button
            className="action-btn flag-btn"
            onClick={() => setActiveModal('FLAG')}
            disabled={loading}
          >
            <span className="btn-icon">🚩</span>
            <div className="btn-content">
              <div className="btn-title">Flag Content</div>
              <div className="btn-description">Report policy violations</div>
            </div>
          </button>

          <button
            className="action-btn vote-btn"
            onClick={() => setActiveModal('VOTE')}
            disabled={loading}
          >
            <span className="btn-icon">🗳️</span>
            <div className="btn-content">
              <div className="btn-title">Vote on Proposals</div>
              <div className="btn-description">{pendingProposals.length} active proposals</div>
            </div>
          </button>

          {adminPermissions?.hasPermission && (
            <button
              className="action-btn admin-btn"
              onClick={() => setActiveModal('ADMIN')}
              disabled={loading}
            >
              <span className="btn-icon">🛡️</span>
              <div className="btn-content">
                <div className="btn-title">Admin Panel</div>
                <div className="btn-description">{moderationQueue.length} items pending</div>
              </div>
            </button>
          )}

          <button
            className="action-btn humanity-btn"
            onClick={() => setActiveModal('HUMANITY')}
            disabled={loading}
          >
            <span className="btn-icon">🤖</span>
            <div className="btn-content">
              <div className="btn-title">Proof of Humanity</div>
              <div className="btn-description">Verify your identity</div>
            </div>
          </button>
        </div>
      </div>

      {/* Active Proposals Summary */}
      {pendingProposals.length > 0 && (
        <div className="dao-proposals-summary">
          <div className="summary-header">
            <h4>🗳️ Active Proposals</h4>
            <span className="proposal-count">{pendingProposals.length} active</span>
          </div>
          
          <div className="proposals-list">
            {pendingProposals.slice(0, 3).map(proposal => (
              <div key={proposal.id} className="proposal-summary">
                <div className="proposal-info">
                  <div className="proposal-title">
                    Flag Review: {proposal.flagData?.category}
                  </div>
                  <div className="proposal-meta">
                    {proposal.votes?.length || 0} votes • {proposal.totalStake || 0} ZNOOZINU staked
                  </div>
                </div>
                <div className="proposal-time">
                  {Math.floor((proposal.endTime - Date.now()) / (1000 * 60 * 60))}h remaining
                </div>
              </div>
            ))}
            
            {pendingProposals.length > 3 && (
              <div className="more-proposals">
                +{pendingProposals.length - 3} more proposals
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="dao-error">
          <span className="error-icon">⚠️</span>
          {error}
          <button className="retry-btn" onClick={loadUserDAOData}>
            Retry
          </button>
        </div>
      )}

      {/* Modals */}
      {activeModal === 'FLAG' && (
        <ContentFlagging
          contentId="example-content-id" // This would be passed from parent component
          onFlagSubmitted={handleFlagSubmitted}
          onClose={handleModalClose}
        />
      )}

      {activeModal === 'VOTE' && (
        <DAOVoting
          onClose={handleModalClose}
        />
      )}

      {activeModal === 'ADMIN' && adminPermissions?.hasPermission && (
        <AdminPanel
          onClose={handleModalClose}
        />
      )}

      {activeModal === 'HUMANITY' && (
        <div className="humanity-modal">
          <div className="humanity-container">
            <div className="humanity-header">
              <h3>🤖 Proof of Humanity</h3>
              <button className="close-btn" onClick={handleModalClose}>×</button>
            </div>
            <div className="humanity-content">
              <p>Proof of Humanity verification system is coming soon!</p>
              <p>This will help prevent bots and ensure authentic community participation.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DAOIntegration;
